<template>
    <v-card :loading="loading">
        <v-card-title ref="title">
            <v-btn small icon @click="close" color="primary" class="mr-2">
                <v-icon>
                    mdi-close
                </v-icon>
            </v-btn>
            <h3>{{ editionView ? 'Edit Package' : 'New Package' }}</h3>
            <v-spacer />
            <v-chip
                v-if="editionView && lockedBy"
                :color="lockChipColor"
                text-color="white"
                small
                class="ml-2"
            >
                <v-icon>mdi-account</v-icon>
                <span>{{ lockChipText }}</span>
            </v-chip>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text
            id="virtual-scroll-table"
            class="px-4 pb-0"
            :style="{ height: `${height}px` }"
        >
            <v-form v-model="valid" class="pt-4">
                <v-row>
                    <v-col cols="12" xl="2" lg="2" md="2" sm="6">
                        <v-autocomplete
                            class="pt-0"
                            v-model="packageDimensions.units"
                            :items="measureUnits"
                            hide-details
                            label="Units"
                            prepend-icon="mdi-ruler"
                            required
                            :rules="[rules.required]"
                            @change="structureData()"
                            :disabled="Boolean(disableUpdates || isLocked)"
                        />
                    </v-col>
                    <v-col cols="12" xl="10" lg="10" md="10" sm="12">
                        <v-row>
                            <v-col cols="12" xl="3" lg="3" md="3" sm="6">
                                <v-text-field
                                    class="pt-0"
                                    v-model="packageDimensions.width"
                                    hide-details
                                    type="number"
                                    label="Package Width"
                                    required
                                    prepend-icon="mdi-arrow-left-right"
                                    :rules="[rules.number]"
                                    @input="structureData()"
                                    :disabled="
                                        Boolean(disableUpdates || isLocked)
                                    "
                                />
                            </v-col>
                            <v-col cols="12" xl="3" lg="3" md="3" sm="6">
                                <v-text-field
                                    class="pt-0"
                                    v-model="packageDimensions.height"
                                    hide-details
                                    type="number"
                                    label="Package Height"
                                    required
                                    prepend-icon="mdi-arrow-up-down"
                                    :rules="[rules.number]"
                                    @input="structureData()"
                                    :disabled="
                                        Boolean(disableUpdates || isLocked)
                                    "
                                />
                            </v-col>
                            <v-col cols="12" xl="3" lg="3" md="3" sm="6">
                                <v-text-field
                                    class="pt-0"
                                    v-model="packageDimensions.depth"
                                    hide-details
                                    type="number"
                                    label="Package Length"
                                    required
                                    prepend-icon="mdi-arrow-expand"
                                    :rules="[rules.number]"
                                    @input="structureData()"
                                    :disabled="
                                        Boolean(disableUpdates || isLocked)
                                    "
                                />
                            </v-col>

                            <v-col cols="12" xl="3" lg="3" md="3" sm="6">
                                <v-text-field
                                    class="pt-0"
                                    v-model="packageDimensions.mass"
                                    hide-details
                                    type="number"
                                    label="Crate Mass"
                                    required
                                    prepend-icon="mdi-weight"
                                    :rules="[rules.number]"
                                    @input="structureData()"
                                    :disabled="
                                        Boolean(disableUpdates || isLocked)
                                    "
                                    suffix="Kg"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col cols="12" xl="3" lg="3" md="3" sm="3" class="my-3">
                        <v-autocomplete
                            v-model="packageBOM"
                            :items="BOMs"
                            item-text="code"
                            label="Package BOM"
                            prepend-icon="mdi-package-variant"
                            hide-details
                            @change="structureData()"
                            :disabled="Boolean(disableUpdates || isLocked)"
                        ></v-autocomplete>
                    </v-col>
                    <v-row cols="12" xl="9" lg="9" md="9" sm="9" class="py-3">
                        <v-col
                            cols="12"
                            xl="6"
                            lg="6"
                            md="6"
                            sm="6"
                            class="py-3"
                        >
                            <v-combobox
                                v-model="selectedWorkOrders"
                                :items="filteredWorkOrders"
                                item-text="code"
                                label="WorkOrders"
                                prepend-icon="mdi-hammer-wrench"
                                multiple
                                chips
                                small-chips
                                @change="structureData()"
                                :disabled="Boolean(disableUpdates || isLocked)"
                            ></v-combobox>
                        </v-col>
                        <v-col
                            cols="12"
                            xl="6"
                            lg="6"
                            md="6"
                            sm="6"
                            class="py-3"
                        >
                            <v-combobox
                                v-model="selectedBOMs"
                                :items="filteredBOMs"
                                item-text="code"
                                label="BOMs"
                                prepend-icon="mdi-list-box-outline"
                                multiple
                                chips
                                small-chips
                                @change="structureData()"
                                :disabled="Boolean(disableUpdates || isLocked)"
                            ></v-combobox>
                        </v-col>
                    </v-row>
                </v-row>
            </v-form>
            <v-container fluid px-0>
                <v-row no-gutters v-if="selectedWorkOrders.length > 0">
                    <v-col
                        cols="12"
                        v-for="(workOrder, i) in selectedWorkOrders"
                        :key="i"
                        class="mb-5"
                    >
                        <v-data-table
                            :headers="headers"
                            :items="sortedItems(workOrder, i)"
                            class="elevation-0"
                            :mobile-breakpoint="0"
                            :loading="loading"
                            disable-pagination
                            hide-default-footer
                            multiple-expand
                            :expanded.sync="workOrder.items"
                        >
                            <template v-slot:top>
                                <v-row
                                    no-gutter
                                    :style="{ 'background-color': '#eeeeee' }"
                                    class="mt-0 mx-0"
                                >
                                    <v-col cols="12" class="d-flex">
                                        <h2 class="mt-1">
                                            WORK ORDER:
                                            {{ workOrder.code }}
                                        </h2>
                                    </v-col>
                                </v-row>
                            </template>
                            <!--HEADERS-->
                            <template v-slot:[`header.partNumber`]="{ header }">
                                <v-text-field
                                    class="pb-2 pt-3 mt-2"
                                    :label="header.text"
                                    v-model="partNumberToSearch[i]"
                                    dense
                                />
                            </template>
                            <!--ITEMS-->
                            <template v-slot:[`item.index`]="{ index }">
                                {{ index + 1 }}
                            </template>
                            <template v-slot:[`item.partNumber`]="{ item }">
                                <p
                                    class="my-0 font-weight-bold text-capitalize"
                                >
                                    {{ item.partNumber }}
                                </p>
                            </template>
                            <template v-slot:[`item.description`]="{ item }">
                                <p
                                    class="my-0 font-weight-bold text-capitalize"
                                >
                                    {{ item.description }}
                                </p>
                            </template>
                            <template v-slot:[`item.mass`]="{ item }">
                                <p
                                    class="my-0 font-weight-bold text-capitalize text-right"
                                >
                                    {{ Number(item.mass || 0).toFixed(1) }} Kg
                                </p>
                            </template>
                            <template v-slot:[`item.quantity`]="{ item }">
                                <p class="my-0 text-right">
                                    {{ item.quantity || 0 }}
                                </p>
                            </template>
                            <template v-slot:[`item.packedQty`]="{ item }">
                                <p class="my-0 text-right">
                                    {{ item.packedQty || 0 }}
                                </p>
                            </template>
                            <template v-slot:[`item.availableQty`]="{ item }">
                                <p class="my-0 text-right">
                                    {{ item.availableQty || 0 }}
                                </p>
                            </template>
                            <template
                                v-slot:[`item.suggestedAvailableQty`]="{ item }"
                            >
                                <p class="my-0 text-right">
                                    {{ item.suggestedAvailableQty }}
                                </p>
                            </template>
                            <template
                                v-slot:[`item.packingQty`]="{ item, index }"
                            >
                                <div class="d-flex justify-end align-center">
                                    <v-text-field
                                        type="number"
                                        v-model="item.packingQty"
                                        :style="{ 'max-width': '50px' }"
                                        :rules="[
                                            maxValue(item),
                                            rules.optionalNumber,
                                        ]"
                                        @input="structureData()"
                                        :disabled="
                                            Boolean(disableUpdates || isLocked)
                                        "
                                    />
                                    <v-btn
                                        icon
                                        @click="
                                            openPackingQtyDialog(
                                                i,
                                                index,
                                                'wo',
                                                item
                                            )
                                        "
                                        :disabled="
                                            Boolean(disableUpdates || isLocked)
                                        "
                                    >
                                        <v-icon color="primary"
                                            >mdi-plus</v-icon
                                        >
                                    </v-btn>
                                </div>
                            </template>
                            <template v-slot:[`item.suggestedQty`]="{ item }">
                                <div class="d-flex justify-end">
                                    <v-text-field
                                        v-if="!suggestedProcess"
                                        type="number"
                                        v-model="item.suggestedQty"
                                        :style="{ 'max-width': '50px' }"
                                        :rules="[
                                            maxSuggestedValue(item),
                                            rules.optionalNumber,
                                        ]"
                                        @input="structureData()"
                                        :disabled="
                                            Boolean(disableUpdates || isLocked)
                                        "
                                    />
                                    <p v-else class="my-0 text-right">
                                        {{ item.suggestedQty }}
                                    </p>
                                </div>
                            </template>
                            <template v-slot:[`item.packingMass`]="{ item }">
                                <p class="my-0 text-right">
                                    {{
                                        (
                                            item.packingQty * item.mass || 0
                                        ).toFixed(1)
                                    }}
                                    Kg
                                </p>
                            </template>
                            <template
                                v-slot:[`item.suggestedPackingMass`]="{ item }"
                            >
                                <p class="my-0 text-right">
                                    {{
                                        (
                                            item.suggestedQty * item.mass || 0
                                        ).toFixed(1)
                                    }}
                                    Kg
                                </p>
                            </template>
                            <template v-slot:[`item.notes`]="{ item }">
                                <div class="d-flex justify-center">
                                    <v-text-field
                                        v-model="item.notes"
                                        @input="structureData()"
                                        :disabled="
                                            Boolean(disableUpdates || isLocked)
                                        "
                                    />
                                </div>
                            </template>
                            <!--FOOTER-->
                            <template v-slot:[`body.append`]>
                                <tr :style="{ 'background-color': '#eeeeee' }">
                                    <td />
                                    <td />
                                    <td />
                                    <td />
                                    <td v-if="!onlySuggested" />
                                    <td v-if="!onlySuggested" />
                                    <td />
                                    <td
                                        class="font-weight-bold"
                                        :style="{ 'text-align': 'right' }"
                                        v-if="suggestedProcess || onlySuggested"
                                    >
                                        {{
                                            packingProcessItems(
                                                workOrder
                                            ).reduce(
                                                (accumulator, currentValue) =>
                                                    Number(accumulator) +
                                                    Number(
                                                        currentValue.suggestedQty ||
                                                            0
                                                    ),
                                                0
                                            ) || 0
                                        }}
                                    </td>
                                    <td
                                        class="font-weight-bold"
                                        :style="{ 'text-align': 'right' }"
                                        v-if="!onlySuggested"
                                    >
                                        {{
                                            packingProcessItems(
                                                workOrder
                                            ).reduce(
                                                (accumulator, currentValue) =>
                                                    Number(accumulator) +
                                                    Number(
                                                        currentValue.packingQty ||
                                                            0
                                                    ),
                                                0
                                            ) || 0
                                        }}
                                    </td>
                                    <td
                                        class="font-weight-bold"
                                        :style="{ 'text-align': 'right' }"
                                    >
                                        {{
                                            (
                                                packingProcessItems(
                                                    workOrder
                                                ).reduce(
                                                    (
                                                        accumulator,
                                                        currentValue
                                                    ) =>
                                                        +accumulator +
                                                        (onlySuggested
                                                            ? +currentValue.suggestedQty *
                                                              (+currentValue.mass ||
                                                                  0)
                                                            : +currentValue.packingQty *
                                                              (+currentValue.mass ||
                                                                  0)),
                                                    0
                                                ) || 0
                                            ).toFixed(1)
                                        }}
                                        Kg
                                    </td>
                                    <td />
                                </tr>
                            </template>
                        </v-data-table>
                    </v-col>
                </v-row>
                <v-row no-gutters v-if="selectedBOMs.length > 0">
                    <v-col
                        cols="12"
                        v-for="(bom, i) in selectedBOMs"
                        :key="i"
                        class="mb-5"
                    >
                        <v-data-table
                            :headers="bomHeaders"
                            :items="sortedBOMItems(bom)"
                            class="elevation-0"
                            :mobile-breakpoint="0"
                            :loading="loading"
                            disable-pagination
                            hide-default-footer
                            multiple-expand
                            :expanded.sync="bom.items"
                        >
                            <template v-slot:top>
                                <v-row
                                    no-gutter
                                    :style="{ 'background-color': '#eeeeee' }"
                                    class="mt-0 mx-0"
                                >
                                    <v-col cols="12" class="d-flex">
                                        <h2 class="mt-1">
                                            BOM:
                                            {{ bom.code }}
                                        </h2>
                                    </v-col>
                                </v-row>
                            </template>
                            <!--ITEMS-->
                            <template v-slot:[`item.index`]="{ index }">
                                {{ index + 1 }}
                            </template>
                            <template v-slot:[`item.code`]="{ item }">
                                <p
                                    class="my-0 font-weight-bold text-capitalize"
                                >
                                    {{ item.code }}
                                </p>
                            </template>
                            <template v-slot:[`item.description`]="{ item }">
                                <p
                                    class="my-0 font-weight-bold text-capitalize"
                                >
                                    {{ item.description }}
                                </p>
                            </template>
                            <template v-slot:[`item.quantity`]="{ item }">
                                <p class="my-0 text-right">
                                    {{ item.quantity || 0 }}
                                </p>
                            </template>
                            <template v-slot:[`item.packedQty`]="{ item }">
                                <p class="my-0 text-right">
                                    {{ item.packedQty || 0 }}
                                </p>
                            </template>
                            <template v-slot:[`item.availableQty`]="{ item }">
                                <p class="my-0 text-right">
                                    {{ item.availableQty || 0 }}
                                </p>
                            </template>
                            <template
                                v-slot:[`item.packingQty`]="{ item, index }"
                            >
                                <div class="d-flex justify-end align-center">
                                    <v-text-field
                                        type="number"
                                        v-model="item.packingQty"
                                        :style="{ 'max-width': '50px' }"
                                        :rules="[
                                            maxValue(item),
                                            rules.optionalNumber,
                                        ]"
                                        @input="structureData()"
                                        :disabled="
                                            Boolean(disableUpdates || isLocked)
                                        "
                                    />
                                    <v-btn
                                        icon
                                        @click="
                                            openPackingQtyDialog(
                                                i,
                                                index,
                                                'bom',
                                                item
                                            )
                                        "
                                        :disabled="
                                            Boolean(disableUpdates || isLocked)
                                        "
                                    >
                                        <v-icon color="primary"
                                            >mdi-plus</v-icon
                                        >
                                    </v-btn>
                                </div>
                            </template>
                            <template v-slot:[`item.notes`]="{ item }">
                                <div class="d-flex justify-center">
                                    <v-text-field
                                        v-model="item.notes"
                                        @input="structureData()"
                                        :disabled="
                                            Boolean(disableUpdates || isLocked)
                                        "
                                    />
                                </div>
                            </template>
                            <template
                                v-slot:[`item.suggestedAvailableQty`]="{ item }"
                            >
                                <p class="my-0 text-right">
                                    {{ item.suggestedAvailableQty }}
                                </p>
                            </template>
                            <template v-slot:[`item.suggestedQty`]="{ item }">
                                <div class="d-flex justify-end">
                                    <v-text-field
                                        v-if="!suggestedProcess"
                                        type="number"
                                        v-model="item.suggestedQty"
                                        :style="{ 'max-width': '50px' }"
                                        :rules="[
                                            maxSuggestedValue(item),
                                            rules.optionalNumber,
                                        ]"
                                        @input="structureData()"
                                        :disabled="
                                            Boolean(disableUpdates || isLocked)
                                        "
                                    />
                                    <p v-else class="my-0 text-right">
                                        {{ item.suggestedQty }}
                                    </p>
                                </div>
                            </template>
                            <template v-slot:[`item.mass`]="{ item }">
                                <div class="d-flex justify-end">
                                    <v-text-field
                                        type="number"
                                        v-model="item.mass"
                                        :style="{ 'max-width': '50px' }"
                                        :rules="[rules.required, rules.number]"
                                        @input="structureData()"
                                    />
                                </div>
                            </template>
                            <template v-slot:[`item.totalMass`]="{ item }">
                                <div class="d-flex justify-end">
                                    <p
                                        class="my-0 text-right"
                                        v-if="onlySuggested"
                                    >
                                        {{
                                            (
                                                (+item.suggestedQty || 0) *
                                                (+item.mass || 0)
                                            ).toFixed(1)
                                        }}
                                        Kg
                                    </p>
                                    <p class="my-0 text-right" v-else>
                                        {{
                                            (
                                                (+item.packingQty || 0) *
                                                (+item.mass || 0)
                                            ).toFixed(1)
                                        }}
                                        Kg
                                    </p>
                                </div>
                            </template>
                            <!--FOOTER-->
                            <template v-slot:[`body.append`]>
                                <tr :style="{ 'background-color': '#eeeeee' }">
                                    <td />
                                    <td />
                                    <td />
                                    <td v-if="!onlySuggested" />
                                    <td v-if="!onlySuggested" />
                                    <td />
                                    <td
                                        class="font-weight-bold"
                                        :style="{ 'text-align': 'right' }"
                                        v-if="suggestedProcess || onlySuggested"
                                    >
                                        {{
                                            bom.items.reduce(
                                                (accumulator, currentValue) =>
                                                    Number(accumulator) +
                                                    Number(
                                                        currentValue.suggestedQty ||
                                                            0
                                                    ),
                                                0
                                            ) || 0
                                        }}
                                    </td>
                                    <td
                                        class="font-weight-bold"
                                        :style="{ 'text-align': 'right' }"
                                        v-if="!onlySuggested"
                                    >
                                        {{
                                            bom.items.reduce(
                                                (accumulator, currentValue) =>
                                                    Number(accumulator) +
                                                    Number(
                                                        currentValue.packingQty ||
                                                            0
                                                    ),
                                                0
                                            ) || 0
                                        }}
                                    </td>
                                    <td />
                                    <td
                                        class="font-weight-bold"
                                        :style="{ 'text-align': 'right' }"
                                        v-if="!onlySuggested"
                                    >
                                        {{
                                            (
                                                bom.items.reduce(
                                                    (
                                                        accumulator,
                                                        currentValue
                                                    ) =>
                                                        +accumulator +
                                                        (+currentValue.packingQty ||
                                                            0) *
                                                            (+currentValue.mass ||
                                                                0),
                                                    0
                                                ) || 0
                                            ).toFixed(1)
                                        }}
                                        Kg
                                    </td>
                                    <td
                                        class="font-weight-bold"
                                        :style="{ 'text-align': 'right' }"
                                        v-if="onlySuggested"
                                    >
                                        {{
                                            (
                                                bom.items.reduce(
                                                    (
                                                        accumulator,
                                                        currentValue
                                                    ) =>
                                                        +accumulator +
                                                        (+currentValue.suggestedQty ||
                                                            0) *
                                                            (+currentValue.mass ||
                                                                0),
                                                    0
                                                ) || 0
                                            ).toFixed(1)
                                        }}
                                        Kg
                                    </td>
                                    <td />
                                </tr>
                            </template>
                        </v-data-table>
                    </v-col>
                </v-row>
                <v-col
                    v-if="
                        selectedWorkOrders.length == 0 &&
                            selectedBOMs.length == 0
                    "
                >
                    <v-divider />
                    <h3 class="d-flex align-center justify-center pt-4">
                        No work Orders or BOMs selected
                    </h3>
                </v-col>
            </v-container>
            <v-row
                no-gutters
                :style="{ 'background-color': '#eeeeee' }"
                class="mt-8"
                ref="totals"
            >
                <v-col cols="12" xl="3" lg="3" md="3">
                    <p class="my-2 font-weight-black ml-4">PACKAGE TOTALS</p>
                </v-col>
                <v-col cols="12" xl="3" lg="3" md="3">
                    <p class="my-2  ml-4 font-weight-black">
                        WORK ORDERS SELECTED:
                        {{ selectedWorkOrders.length }}
                    </p>
                </v-col>
                <v-col cols="12" xl="2" lg="2" md="2">
                    <p class="my-2  ml-4 font-weight-black">
                        BOMS SELECTED:
                        {{ selectedBOMs.length }}
                    </p>
                </v-col>
                <v-col cols="12" xl="2" lg="2" md="2">
                    <p class="my-2  ml-4 font-weight-black">
                        PACKAGE ITEMS: {{ packageTotalItems() }}
                    </p>
                </v-col>
                <v-col cols="12" xl="2" lg="2" md="2">
                    <p class="my-2  ml-4 font-weight-black">
                        TOTAL MASS: {{ packageTotalMass().toFixed(1) }} Kg
                    </p>
                </v-col>
            </v-row>
        </v-card-text>
        <v-card-actions ref="actions">
            <v-spacer />
            <v-btn
                color="primary"
                class="mb-4"
                @click="addPackage"
                rounded
                :disabled="isActionDisabled || loading || isLocked"
            >
                {{ editionView ? 'SAVE' : 'ADD PACKAGE' }}
            </v-btn>
        </v-card-actions>
        <!-- ADD PACKING QTY -->
        <v-dialog
            v-model="packingQtyDialog"
            :retain-focus="false"
            persistent
            max-width="400"
        >
            <v-card :loading="loading">
                <v-card-title class="text-h5">
                    <v-btn
                        small
                        icon
                        @click="closePackingQtyDialog"
                        color="primary"
                        class="mr-2"
                    >
                        <v-icon>
                            mdi-close
                        </v-icon>
                    </v-btn>
                    <h3>
                        Update Packing Quantity
                    </h3></v-card-title
                >
                <v-divider class="ml-4 mr-5" />
                <v-card-text class="pt-6 pb-0">
                    <v-form v-model="validPacking" :disabled="isLocked"
                        ><v-row>
                            <v-col cols="3">
                                <p
                                    class="d-flex justify-center align-center my-0 py-0"
                                    style="font-size: 25px;"
                                >
                                    {{ selectedItem.packingQty || 0 }}
                                </p>
                            </v-col>
                            <v-col cols="1">
                                <p
                                    class="d-flex justify-center align-center my-0 py-0"
                                    style="font-size: 25px;"
                                >
                                    +
                                </p>
                            </v-col>
                            <v-col cols="3" class="pt-2"
                                ><v-text-field
                                    type="number"
                                    v-model="selectedItem.qtyToAdd"
                                    class="d-flex justify-center align-center my-0 py-0"
                                    style="font-size: 25px;"
                                    :rules="[
                                        () => maxUpdateValue(selectedItem),
                                        rules.required,
                                        rules.number,
                                    ]"
                            /></v-col>
                            <v-col cols="1">
                                <p
                                    class="d-flex justify-center align-center my-0 py-0"
                                    style="font-size: 25px;"
                                >
                                    =
                                </p>
                            </v-col>
                            <v-col cols="3">
                                <p
                                    class="d-flex justify-center align-center my-0 py-0"
                                    style="font-size: 25px;"
                                >
                                    {{
                                        Number(selectedItem.packingQty || 0) +
                                            Number(selectedItem.qtyToAdd || 0)
                                    }}
                                </p>
                            </v-col>
                        </v-row></v-form
                    >
                </v-card-text>
                <v-spacer></v-spacer>
                <v-card-actions class="pb-4">
                    <v-spacer />
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="
                            updatePackingQty(
                                Number(selectedItem.packingQty || 0) +
                                    Number(selectedItem.qtyToAdd)
                            )
                        "
                        color="primary"
                        :loading="loading"
                        rounded
                        :disabled="!validPacking"
                    >
                        SAVE
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Conflict dialog -->
        <v-dialog v-model="conflictDialog" persistent max-width="520">
            <v-card>
                <v-card-title class="text-h6">
                    Package conflict
                </v-card-title>
                <v-card-text>
                    <template v-if="conflictType === 'quantity'">
                        <p class="mb-2">
                            The following items exceed the available quantities:
                        </p>
                        <v-list dense class="mb-3">
                            <v-list-item
                                v-for="(err, i) in conflictErrors"
                                :key="i"
                                class="px-0"
                            >
                                <v-list-item-icon class="mr-2 my-auto">
                                    <v-icon small color="error">mdi-alert-circle</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title class="text-body-2 text-wrap">
                                        {{ err }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                        <p class="text-caption text--secondary mb-0">
                            This may have been caused by another user saving a package
                            at the same time. Please review the available quantities
                            and adjust your package before saving again.
                        </p>
                    </template>
                    <template v-else>
                        {{ conflictMessage }}
                    </template>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <template v-if="conflictType === 'quantity'">
                        <v-btn color="primary" rounded @click="conflictDialog = false">
                            Close
                        </v-btn>
                    </template>
                    <template v-else>
                        <v-btn text @click="reloadFromServer">
                            Reload changes
                        </v-btn>
                        <v-btn color="primary" rounded @click="retryAfterConflict">
                            Force save (overwrite)
                        </v-btn>
                    </template>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--Error Alert-->
        <Errors />
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import _ from 'lodash'
import { objDiff } from '@/helpers/objDiff.js'
import API from '@/services/api'
import { firestore } from '@/services/firebase'

export default {
    name: 'PackageForm',
    props: {
        projectWorkOrders: {
            type: Array,
            required: true,
            default: () => [],
        },
        BOMs: {
            type: Array,
            required: true,
            default: () => [],
        },
        measureUnits: {
            type: Array,
            required: true,
            default: () => [],
        },
        editionView: {
            type: Boolean,
            required: true,
            default: () => false,
        },
        originalPackage: {
            type: Object,
            required: false,
            default: () => {},
        },
        packingList: {
            type: Object,
            required: false,
            default: () => {},
        },
        onlySuggested: {
            type: Boolean,
            required: true,
            default: () => true,
        },
    },
    components: {
        Errors: () => import('@/components/Layout/Errors'),
    },
    data: () => ({
        loading: false,
        valid: false,
        validPacking: false,
        selectedBOMs: [],
        selectedWorkOrders: [],
        filteredWorkOrders: [],
        filteredBOMs: [],
        packageDimensions: {
            units: null,
            width: null,
            height: null,
            depth: null,
            mass: null,
        },
        packageBOM: null,
        rules: {
            optionalNumber: v =>
                (isNaN(v) && v != '') || Number(v) >= 0 || 'Invalid number',
            required: v => !!v || 'Required',
            number: v => (!isNaN(v) && Number(v) > 0) || 'Invalid number',
        },
        headers: [
            {
                text: 'INDEX',
                value: 'index',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PART NUMBER',
                value: 'partNumber',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'UNIT MASS',
                value: 'mass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'QTY',
                value: 'quantity',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKED QTY',
                value: 'packedQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'AVAILABLE QTY',
                value: 'availableQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING QTY',
                value: 'packingQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING MASS',
                value: 'packingMass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'left',
                sortable: false,
            },
        ],
        bomHeaders: [
            {
                text: 'INDEX',
                value: 'index',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CODE',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'QTY',
                value: 'quantity',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKED QTY',
                value: 'packedQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'AVAILABLE QTY',
                value: 'availableQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING QTY',
                value: 'packingQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'UNIT MASS(Kg)',
                value: 'mass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING MASS',
                value: 'totalMass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'left',
                sortable: false,
            },
        ],
        suggestedHeaders: [
            {
                text: 'INDEX',
                value: 'index',
                align: 'center',
                sortable: false,
            },
            {
                text: 'PART NUMBER',
                value: 'partNumber',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'UNIT MASS',
                value: 'mass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'AVAILABLE QTY',
                value: 'suggestedAvailableQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'SUGGESTED QTY',
                value: 'suggestedQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING MASS',
                value: 'suggestedPackingMass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'left',
                sortable: false,
            },
        ],
        suggestedBomHeaders: [
            {
                text: 'INDEX',
                value: 'index',
                align: 'center',
                sortable: false,
            },
            {
                text: 'CODE',
                value: 'code',
                align: 'center',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                align: 'left',
                sortable: false,
            },
            {
                text: 'AVAILABLE QTY',
                value: 'suggestedAvailableQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'SUGGESTED QTY',
                value: 'suggestedQty',
                align: 'right',
                sortable: false,
            },
            {
                text: 'UNIT MASS(Kg)',
                value: 'mass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'PACKING MASS',
                value: 'totalMass',
                align: 'right',
                sortable: false,
            },
            {
                text: 'NOTES',
                value: 'notes',
                align: 'left',
                sortable: false,
            },
        ],
        height: 0,
        structuredData: {},
        WOs: [],
        disableUpdates: false,
        suggestedProcess: false,
        packingQtyDialog: false,
        selectedParentIndex: -1,
        selectedItemIndex: -1,
        selectedType: '',
        selectedItem: {},
        partNumberToSearch: [],
        disableSelectedWorkOrders: false,
        currentUserId: null,
        user: {},
        packageLockListener: null,
        lockedBy: null,
        isLockedByOtherUser: false,
        lockUpdateTimeout: null,
        lockHeartbeatInterval: null,
        isDestroying: false,
        lockAcquireFailed: false,
        conflictDialog: false,
        conflictMessage: '',
        conflictType: 'lock', // 'lock' | 'quantity'
        conflictErrors: [],
        isSyncingExternalUpdate: false,
        lockShouldBeHeld: false,
        companyId: JSON.parse(localStorage.getItem('company')),
    }),
    computed: {
        packageDiff: function() {
            if (this.isSyncingExternalUpdate) {
                return {}
            }
            const packageData = this.structuredData
            if (this.originalPackage && this.originalPackage.items) {
                this.originalPackage.items.forEach(
                    item => (item.packingQty = Number(item.packingQty))
                )
                return objDiff(this.originalPackage, packageData)
            } else {
                return {}
            }
        },
        hasPendingChanges() {
            if (!this.editionView) return false
            return (
                !this.isActionDisabled &&
                Object.keys(this.packageDiff).length > 0
            )
        },
        isActionDisabled() {
            let validWO = false
            let validBOM = false
            if (this.onlySuggested) {
                validWO = this.selectedWorkOrders.every(workOrder => {
                    const items = this.packingProcessItems(workOrder)
                    return (
                        items.some(item => item.suggestedQty > 0) &&
                        items.every(
                            item =>
                                item.suggestedQty === undefined ||
                                item.suggestedQty === '' ||
                                item.suggestedQty === null ||
                                item.suggestedQty == 0 ||
                                (item.suggestedQty > 0 &&
                                    Number(item.suggestedQty || 0) <=
                                        item.suggestedAvailableQty)
                        )
                    )
                })

                validBOM = this.selectedBOMs.every(
                    bom =>
                        bom.items.some(item => item.suggestedQty > 0) &&
                        bom.items.every(
                            item =>
                                item.suggestedQty === undefined ||
                                item.suggestedQty === '' ||
                                item.suggestedQty === null ||
                                item.suggestedQty == 0 ||
                                (item.suggestedQty > 0 &&
                                    Number(item.suggestedQty || 0) <=
                                        item.suggestedAvailableQty &&
                                    item.mass &&
                                    item.mass > 0)
                        )
                )
            } else {
                validWO = this.selectedWorkOrders.every(workOrder => {
                    const items = this.packingProcessItems(workOrder)
                    const validation1 = items.some(
                        item => item.packingQty > 0 || item.suggestedQty > 0
                    )
                    const validation2 = items.every(item => {
                        return (
                            item.packingQty == undefined ||
                            item.packingQty == '' ||
                            item.packingQty == 0 ||
                            (item.packingQty > 0 &&
                                Number(item.packingQty || 0) <=
                                    item.availableQty)
                        )
                    })
                    return validation1 && validation2
                })

                validBOM = this.selectedBOMs.every(bom => {
                    const validation1 = bom.items.some(
                        item => item.packingQty > 0 || item.suggestedQty > 0
                    )
                    const validation2 = bom.items.every(item => {
                        return (
                            item.packingQty == undefined ||
                            item.packingQty == '' ||
                            item.packingQty == 0 ||
                            (item.packingQty > 0 &&
                                Number(item.packingQty || 0) <=
                                    item.availableQty &&
                                item.mass &&
                                item.mass > 0)
                        )
                    })
                    return validation1 && validation2
                })

                if (this.selectedBOMs.length == 0) {
                    validBOM = true
                }
            }

            const validation =
                !this.valid ||
                (this.selectedWorkOrders.length == 0 &&
                    this.selectedBOMs.length == 0) ||
                (Object.keys(this.packageDiff).length == 0 &&
                    Object.keys(this.originalPackage || {}).length > 0)
            return validation || !validWO || !validBOM
        },
        isLocked() {
            return (
                this.lockAcquireFailed ||
                !!(this.isLockedByOtherUser && this.lockedBy)
            )
        },
        lockChipColor() {
            return this.isLockedByOtherUser ? 'warning' : 'info'
        },
        lockChipText() {
            return this.isLockedByOtherUser
                ? `${this.lockedBy.userName} is editing`
                : 'editing...'
        },
    },
    watch: {
        hasPendingChanges(newVal, oldVal) {
            if (newVal && !oldVal) {
                this.lockShouldBeHeld = true
                this.updatePackageLock(true)
            } else if (!newVal && oldVal && !this.loading) {
                this.lockShouldBeHeld = false
                this.updatePackageLock(false)
            }
        },
        isLockedByOtherUser(newVal, oldVal) {
            if (!newVal && oldVal) {
                this.lockAcquireFailed = false
                if (this.editionView && !this.isDestroying) {
                    this.acquirePackageLockOnOpen()
                }
            }
        },
        originalPackage: {
            handler(newPackage, oldPackage) {
                if (
                    this.editionView &&
                    newPackage &&
                    newPackage.id &&
                    oldPackage &&
                    oldPackage.id &&
                    newPackage.id === oldPackage.id
                ) {
                    if (
                        this.packingList &&
                        this.packingList.updatedBy === this.currentUserId
                    ) {
                        return
                    }

                    if (this.hasPendingChanges) {
                        return
                    }

                    const oldItemsStr = JSON.stringify(oldPackage.items || [])
                    const newItemsStr = JSON.stringify(newPackage.items || [])
                    const itemsChanged = oldItemsStr !== newItemsStr

                    const dimensionsChanged = !_.isEqual(
                        oldPackage.packageDimensions,
                        newPackage.packageDimensions
                    )
                    const bomChanged =
                        oldPackage.packageBOM !== newPackage.packageBOM

                    if (itemsChanged || dimensionsChanged || bomChanged) {
                        this.recalculatePackageData(newPackage)
                    }
                }
            },
            deep: true,
            immediate: false,
        },
        packingList: {
            async handler(newPackingList, oldPackingList) {
                if (!oldPackingList) {
                    return
                }

                if (newPackingList.updatedBy === this.currentUserId) {
                    return
                }

                if (this.hasPendingChanges || this.loading) {
                    return
                }

                const statusChanged =
                    newPackingList.status !== oldPackingList.status

                // close edit dialog in some statuses
                if (
                    statusChanged &&
                    ![
                        'domesticShipping',
                        'reserved',
                        'reservedForProject',
                        'inProgress',
                        'rejected',
                        'pending',
                    ].includes(newPackingList.status)
                ) {
                    this.close()
                }
            },
            deep: true,
            immediate: false,
        },
    },
    async mounted() {
        try {
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            this.currentUserId = user.id

            if (
                this.editionView &&
                this.originalPackage &&
                this.originalPackage.id &&
                this.packingList &&
                this.packingList.id
            ) {
                await this.setupPackageLockListener()
                await this.acquirePackageLockOnOpen()
                this.startLockHeartbeat()
            }

            this.WOs = _.cloneDeep(this.projectWorkOrders)

            // if (
            //     this.packingList.status == 'domesticShipping' ||
            //     this.packingList.status == 'reserved' ||
            //     this.packingList.status == 'reservedForProject'
            // ) {
            //     // get the codes of the selected work orders
            //     const selectedCodes = new Set(
            //         this.packingList.selectedWorkOrders.map(
            //             workOrder => workOrder.code
            //         )
            //     )
            //     // set selected work orders
            //     this.selectedWorkOrders = this.WOs.filter(workOrder =>
            //         selectedCodes.has(workOrder.code)
            //     )
            //     // dont allow updates
            //     this.disableSelectedWorkOrders = true
            // }

            this.initWorkOrders()

            if (
                this.packingList.status == 'reserved' ||
                this.packingList.reservedWorkOrders
            ) {
                const restrictedCodes = new Set()
                const reservedWorkOrders =
                    [
                        ...(this.packingList?.reservedWorkOrders || []),
                        ...(this.packingList?.selectedWorkOrders || []),
                    ] || []
                if (reservedWorkOrders.length > 0) {
                    reservedWorkOrders.forEach(wo => {
                        if (wo && wo.code) {
                            restrictedCodes.add(wo.code)
                        }
                    })
                }
                this.filteredWorkOrders = this.filteredWorkOrders.filter(wo =>
                    restrictedCodes.has(wo.code)
                )
            }
            this.initBOMs()
            if (this.originalPackage) {
                this.packageDimensions = _.cloneDeep(
                    this.originalPackage.packageDimensions
                )
                this.packageBOM = this.originalPackage.packageBOM
            }
            if (this.packingList.status == 'pending') {
                this.disableUpdates = true
            }
            if (this.onlySuggested) {
                this.headers = this.suggestedHeaders
                this.bomHeaders = this.suggestedBomHeaders
            } else if (this.originalPackage) {
                this.suggestedProcess = this.originalPackage.items.some(
                    item => item.suggestedQty
                )

                if (this.suggestedProcess) {
                    const suggestedHeader = {
                        text: 'SUGGESTED QTY',
                        value: 'suggestedQty',
                        align: 'right',
                        sortable: false,
                    }
                    this.headers.splice(7, 0, suggestedHeader)
                    this.bomHeaders.splice(4, 0, suggestedHeader)
                }
            }
            this.onResize()
        } catch (error) {
            console.log('error', error)
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        this.isDestroying = true
        this.lockShouldBeHeld = false
        clearTimeout(this.lockUpdateTimeout)
        this.clearLockHeartbeat()
        if (this.packageLockListener) {
            this.packageLockListener()
            this.packageLockListener = null
        }
        if (
            this.editionView &&
            this.originalPackage?.id &&
            this.packingList?.id
        ) {
            this.updatePackageLock(false, true)
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),

        recalculatePackageData(updatedPackage) {
            const isExternalUpdate =
                this.packingList &&
                this.packingList.updatedBy !== this.currentUserId

            if (updatedPackage.packageDimensions) {
                this.packageDimensions = _.cloneDeep(
                    updatedPackage.packageDimensions
                )
            }

            if (updatedPackage.packageBOM !== undefined) {
                this.packageBOM = updatedPackage.packageBOM
            }

            this.WOs = _.cloneDeep(this.projectWorkOrders)

            if (isExternalUpdate) {
                this.isSyncingExternalUpdate = true
            }

            this.$nextTick(() => {
                this.initWorkOrders()
                this.initBOMs()

                if (isExternalUpdate) {
                    const normalizedItems = (updatedPackage.items || []).map(
                        item => {
                            const normalized = { ...item }
                            if (normalized.packingQty !== undefined) {
                                normalized.packingQty = Number(
                                    normalized.packingQty
                                )
                            }
                            if (normalized.suggestedQty !== undefined) {
                                normalized.suggestedQty = Number(
                                    normalized.suggestedQty
                                )
                            }
                            return normalized
                        }
                    )
                    this.structuredData = {
                        items: normalizedItems,
                        packageDimensions: _.cloneDeep(
                            updatedPackage.packageDimensions || {}
                        ),
                        packageBOM: updatedPackage.packageBOM,
                    }

                    this.$nextTick(() => {
                        this.isSyncingExternalUpdate = false
                    })
                } else {
                    this.structureData()
                }
                this.$forceUpdate()
            })
        },

        getPackageLockDocRef() {
            return firestore
                .collection('companies')
                .doc(this.companyId)
                .collection('packingLists')
                .doc(this.packingList.id)
                .collection('packageEditing')
                .doc(this.originalPackage.id)
        },
        applyLockFromSnapshot(snapshot) {
            if (!snapshot.exists) {
                this.lockedBy = null
                this.isLockedByOtherUser = false
                return
            }

            const data = snapshot.data()
            const lockInfo = data?.lockedBy

            if (lockInfo && lockInfo.userId) {
                if (lockInfo.userId !== this.currentUserId) {
                    this.lockedBy = lockInfo
                    this.isLockedByOtherUser = true
                } else {
                    this.lockedBy = lockInfo
                    this.isLockedByOtherUser = false
                }
            } else {
                this.lockedBy = null
                this.isLockedByOtherUser = false
            }
        },
        async refreshPackageLockState({ preferServer = false } = {}) {
            try {
                const docRef = this.getPackageLockDocRef()
                const snapshot = preferServer
                    ? await docRef.get({ source: 'server' })
                    : await docRef.get()
                this.applyLockFromSnapshot(snapshot)
            } catch (error) {
                console.error('Error refreshing package lock state:', error)
            }
        },
        applyLockConflictFromError(error) {
            const lockedBy = error.response?.data?.lockedBy
            if (lockedBy?.userId) {
                this.lockedBy = lockedBy
                this.isLockedByOtherUser =
                    lockedBy.userId !== this.currentUserId
            }
        },

        async setupPackageLockListener() {
            try {
                const docRef = this.getPackageLockDocRef()

                return new Promise(resolve => {
                    let firstSnapshot = true

                    const finishFirstSnapshot = () => {
                        if (firstSnapshot) {
                            firstSnapshot = false
                            resolve()
                        }
                    }

                    this.packageLockListener = docRef.onSnapshot(
                        snapshot => {
                            this.applyLockFromSnapshot(snapshot)
                            finishFirstSnapshot()
                        },
                        error => {
                            console.error(
                                'Error listening to package lock:',
                                error
                            )
                            finishFirstSnapshot()
                        }
                    )
                })
            } catch (error) {
                console.error('Error setting up package lock listener:', error)
            }
        },
        async updatePackageLock(lockStatus, immediate = false) {
            clearTimeout(this.lockUpdateTimeout)

            if (!lockStatus) {
                this.lockShouldBeHeld = false
            }

            const performLock = async () => {
                if (this.isDestroying) {
                    return
                }
                if (lockStatus && this.isLockedByOtherUser) {
                    return
                }
                try {
                    await this.executePackageLock(lockStatus)
                } catch (error) {
                    if (this.isConflictError(error) && lockStatus) {
                        this.applyLockConflictFromError(error)
                        this.lockAcquireFailed = true
                        this.setErrorItems({
                            source: this.$options.name,
                            message: error.message,
                        })
                    } else if (!this.isConflictError(error)) {
                        console.error('Error updating package lock:', error)
                    }
                }
            }

            if (immediate) {
                await performLock()
            } else {
                this.lockUpdateTimeout = setTimeout(performLock, 500)
            }
        },
        async executePackageLock(lockStatus) {
            await API.updatePackageLock({
                packingListId: this.packingList.id,
                packageId: this.originalPackage.id,
                lockStatus,
            })
            if (lockStatus) {
                this.lockAcquireFailed = false
                this.lockShouldBeHeld = true
            }
        },
        async acquirePackageLockOnOpen() {
            await this.refreshPackageLockState({ preferServer: true })
            if (this.isLockedByOtherUser) {
                return
            }
            this.lockShouldBeHeld = true
            await this.updatePackageLock(true, true)
        },
        startLockHeartbeat() {
            if (!this.editionView) {
                return
            }
            this.clearLockHeartbeat()
            this.lockHeartbeatInterval = setInterval(() => {
                if (
                    this.editionView &&
                    this.lockShouldBeHeld &&
                    !this.lockAcquireFailed &&
                    !this.isDestroying
                ) {
                    this.updatePackageLock(true, true)
                }
            }, 2 * 60 * 1000)
        },
        clearLockHeartbeat() {
            if (this.lockHeartbeatInterval) {
                clearInterval(this.lockHeartbeatInterval)
                this.lockHeartbeatInterval = null
            }
        },
        isConflictError(error) {
            return error.status === 409 || error.response?.status === 409
        },
        handleSaveConflict(error) {
            const responseData = error.response?.data
            const apiErrors = responseData?.errors

            if (apiErrors?.length && !responseData?.lockedBy) {
                this.conflictType = 'quantity'
                this.conflictErrors = apiErrors
            } else {
                this.conflictType = 'lock'
                this.conflictErrors = []
                const baseMessage =
                    error.message || 'This package was modified by another user.'
                this.conflictMessage = `${baseMessage} Forcing save will overwrite their changes.`
            }
            this.conflictDialog = true
        },
        reloadFromServer() {
            this.conflictDialog = false
            if (this.originalPackage?.id && this.packingList?.packages) {
                const serverPackage = this.packingList.packages.find(
                    p => p.id === this.originalPackage.id
                )
                if (serverPackage) {
                    this.recalculatePackageData(serverPackage)
                    this.$emit('replacePackage', {
                        packageData: serverPackage,
                    })
                }
            }
            this.$emit('recalculateBalances')
        },
        async retryAfterConflict() {
            this.conflictDialog = false
            this.$emit('recalculateBalances')
            await this.$nextTick()
            await this.addPackage()
        },
        onResize() {
            const {
                title: { clientHeight: titleHeight },
                actions: { clientHeight: actionsHeight },
            } = this.$refs
            this.height = window.innerHeight - titleHeight - actionsHeight - 10
        },
        initWorkOrders() {
            // filter work orders with items
            this.filteredWorkOrders = this.WOs.filter(
                workOrder => workOrder.items
            )
            if (this.originalPackage) {
                const codes = this.originalPackage.items.map(i => i.workOrder)
                //update orders in package
                this.selectedWorkOrders = this.filteredWorkOrders.filter(wo =>
                    codes.includes(wo.code)
                )

                const packageItems = this.originalPackage.items

                this.selectedWorkOrders.forEach(initialWO => {
                    initialWO.items.forEach(woItem => {
                        woItem.suggestedQty = null
                    })
                })

                this.filteredWorkOrders.forEach(wo => {
                    wo.items.forEach(item => {
                        let newAvailableQty = 0
                        let suggestedAvailableQty = 0

                        const index = packageItems.findIndex(
                            i =>
                                i.partNumberId == item.partNumberId &&
                                i.workOrder == wo.code
                        )

                        if (index > -1) {
                            item.notes = packageItems[index].notes
                            newAvailableQty =
                                Number(item.availableQty || 0) +
                                Number(packageItems[index].packingQty || 0)

                            suggestedAvailableQty =
                                Number(item.availableSuggested || 0) +
                                Number(packageItems[index].suggestedQty || 0)

                            this.$set(
                                item,
                                'packingQty',
                                packageItems[index].packingQty
                                    ? Number(packageItems[index].packingQty)
                                    : null
                            )

                            this.$set(
                                item,
                                'suggestedQty',
                                Number(packageItems[index].suggestedQty)
                            )
                        } else {
                            newAvailableQty = Number(item.availableQty)
                            suggestedAvailableQty = Number(
                                item.availableSuggested || 0
                            )
                            this.$set(item, 'suggestedQty', null)
                            this.$set(item, 'packingQty', null)
                        }

                        item.availableQty = newAvailableQty
                        item.suggestedAvailableQty =
                            suggestedAvailableQty > -1
                                ? suggestedAvailableQty
                                : 0
                    })
                })
            } else {
                this.filteredWorkOrders.forEach(wo => {
                    wo.items.forEach(item => {
                        const newAvailableQty = Number(item.availableQty)

                        item.availableQty = newAvailableQty

                        const suggestedAvailableQty = Number(
                            item.availableSuggested || 0
                        )
                        item.suggestedQty = null
                        item.suggestedAvailableQty =
                            suggestedAvailableQty > -1
                                ? suggestedAvailableQty
                                : 0
                    })
                })
            }
        },
        initBOMs() {
            this.filteredBOMs = this.BOMs.filter(bom => bom.items)
            if (this.originalPackage) {
                const codes = this.originalPackage.items.map(i => i.bom)
                this.selectedBOMs = this.filteredBOMs.filter(bom =>
                    codes.includes(bom.code)
                )
                this.selectedBOMs.forEach(initialBOM => {
                    initialBOM.items.forEach(bomItem => {
                        bomItem.suggestedQty = null
                    })
                })

                const packageItems = this.originalPackage.items

                this.filteredBOMs.forEach(bom => {
                    bom.items.forEach(item => {
                        let newAvailableQty = 0
                        let suggestedAvailableQty = 0

                        const index = packageItems.findIndex(
                            i => i.code == item.code && i.bom == bom.code
                        )

                        if (index > -1) {
                            item.notes = packageItems[index].notes
                            item.mass = +packageItems[index].mass
                            newAvailableQty =
                                Number(item.availableQty || 0) +
                                Number(packageItems[index].packingQty || 0)

                            suggestedAvailableQty =
                                Number(item.availableSuggested || 0) +
                                Number(packageItems[index].suggestedQty || 0)

                            item.packingQty = packageItems[index].packingQty
                                ? Number(packageItems[index].packingQty)
                                : null

                            item.suggestedQty = Number(
                                packageItems[index].suggestedQty
                            )
                        } else {
                            newAvailableQty = Number(item.availableQty)
                            suggestedAvailableQty = Number(
                                item.availableSuggested || 0
                            )
                            item.suggestedQty = null
                            item.packingQty = null
                        }

                        item.availableQty = newAvailableQty
                        item.suggestedAvailableQty =
                            suggestedAvailableQty > -1
                                ? suggestedAvailableQty
                                : 0
                    })
                })
            } else {
                this.filteredBOMs.forEach(bom => {
                    bom.items.forEach(item => {
                        const newAvailableQty = Number(item.availableQty)
                        item.availableQty = newAvailableQty

                        const suggestedAvailableQty = Number(
                            item.availableSuggested || 0
                        )
                        item.suggestedQty = null
                        item.suggestedAvailableQty =
                            suggestedAvailableQty > -1
                                ? suggestedAvailableQty
                                : 0
                    })
                })
            }
        },
        maxValue(item) {
            if (item.availableQty < item.packingQty) {
                return `The value cannot be greater than ${item.availableQty}.`
            } else {
                return true
            }
        },
        maxSuggestedValue(item) {
            if (item.suggestedAvailableQty < item.suggestedQty) {
                return `The value cannot be greater than ${item.suggestedAvailableQty}.`
            } else {
                return true
            }
        },
        maxUpdateValue(item) {
            const value =
                Number(item.packingQty || 0) + Number(item.qtyToAdd || 0)

            if (item.suggestedQty) {
                if (value > item.suggestedQty) {
                    return `The value cannot be greater than ${Number(
                        item.suggestedQty
                    ) - Number(item.packingQty || 0)}.`
                } else {
                    return true
                }
            }

            if (value > item.availableQty) {
                return `The value cannot be greater than ${Number(
                    item.availableQty
                ) - Number(item.packingQty || 0)}.`
            } else {
                return true
            }
        },
        packageTotalItems() {
            if (this.onlySuggested) {
                const totalInWO = this.selectedWorkOrders
                    .flatMap(workOrder => workOrder.items)
                    .filter(item => item.suggestedQty)
                    .reduce(
                        (accumulator, currentItem) =>
                            accumulator + Number(currentItem.suggestedQty || 0),
                        0
                    )

                const totalInBOM = this.selectedBOMs
                    .flatMap(bom => bom.items)
                    .filter(item => item.suggestedQty)
                    .reduce(
                        (accumulator, currentItem) =>
                            accumulator + Number(currentItem.suggestedQty || 0),
                        0
                    )

                return totalInBOM + totalInWO
            } else {
                const totalInWO = this.selectedWorkOrders
                    .flatMap(workOrder => workOrder.items)
                    .filter(item => item.packingQty)
                    .reduce(
                        (accumulator, currentItem) =>
                            accumulator + Number(currentItem.packingQty || 0),
                        0
                    )

                const totalInBOM = this.selectedBOMs
                    .flatMap(bom => bom.items)
                    .filter(item => item.packingQty)
                    .reduce(
                        (accumulator, currentItem) =>
                            accumulator + Number(currentItem.packingQty || 0),
                        0
                    )

                return totalInBOM + totalInWO
            }
        },
        packageTotalMass() {
            if (this.onlySuggested) {
                const woMass = this.selectedWorkOrders
                    .flatMap(workOrder => workOrder.items)
                    .filter(item => item.suggestedQty)
                    .reduce(
                        (accumulator, currentItem) =>
                            accumulator +
                            Number(
                                currentItem.suggestedQty * currentItem.mass || 0
                            ),
                        0
                    )
                const bomMass = this.selectedBOMs
                    .flatMap(bom => bom.items)
                    .filter(item => item.suggestedQty)
                    .reduce(
                        (accumulator, currentItem) =>
                            accumulator +
                            Number(
                                currentItem.suggestedQty * currentItem.mass || 0
                            ),
                        0
                    )
                return woMass + bomMass
            } else {
                const woMass = this.selectedWorkOrders
                    .flatMap(workOrder => workOrder.items)
                    .filter(item => item.packingQty)
                    .reduce(
                        (accumulator, currentItem) =>
                            accumulator +
                            Number(
                                currentItem.packingQty * currentItem.mass || 0
                            ),
                        0
                    )
                const bomMass = this.selectedBOMs
                    .flatMap(bom => bom.items)
                    .filter(item => item.packingQty)
                    .reduce(
                        (accumulator, currentItem) =>
                            accumulator +
                            Number(
                                currentItem.packingQty * currentItem.mass || 0
                            ),
                        0
                    )
                return woMass + bomMass
            }
        },
        async addPackage() {
            try {
                const packageData = this.structureData()
                if (this.editionView) {
                    if (this.packingList?.id) {
                        await this.updatePackage(packageData)
                    } else {
                        this.$emit('replacePackage', {
                            packageData,
                        })
                        this.close()
                    }
                } else {
                    if (this.packingList?.id) {
                        await this.createPackage(packageData)
                    } else {
                        this.$emit('addPackage', {
                            newPackage: packageData,
                        })
                    }
                }
            } catch (error) {
                if (!this.isConflictError(error)) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                }
            }
        },
        async createPackage(packageData) {
            try {
                this.loading = true
                const {
                    newPackage,
                    packages,
                    reservedWorkOrders,
                } = await API.createPackage({
                    useSuggested: this.onlySuggested,
                    projectId: this.packingList.projectId,
                    packingListId: this.packingList.id,
                    packageData,
                })
                this.$emit('addPackage', {
                    newPackage,
                    packages,
                    reservedWorkOrders,
                })
                this.close()
            } catch (error) {
                if (this.isConflictError(error)) {
                    this.handleSaveConflict(error)
                } else {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                }
            } finally {
                this.loading = false
            }
        },
        async updatePackage(packageData) {
            try {
                this.loading = true
                packageData.id = this.originalPackage.id
                const newPackages = await API.updatePackage({
                    useSuggested: this.onlySuggested,
                    projectId: this.packingList.projectId,
                    packingListId: this.packingList.id,
                    packageData,
                })

                const updatedPackage = newPackages.find(
                    p => p.id == packageData.id
                )
                this.$emit('replacePackage', {
                    packageData: updatedPackage,
                })
                this.close()
            } catch (error) {
                if (this.isConflictError(error)) {
                    this.handleSaveConflict(error)
                    this.$emit('recalculateBalances')
                } else {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: error.message,
                    })
                }
            } finally {
                this.loading = false
            }
        },
        close() {
            this.lockShouldBeHeld = false
            this.clearLockHeartbeat()
            if (
                this.editionView &&
                this.originalPackage?.id &&
                this.packingList?.id
            ) {
                this.updatePackageLock(false, true)
            }
            this.$emit('close')
        },
        structureData() {
            let packageWOItems = this.selectedWorkOrders.flatMap(workOrder => {
                return workOrder.items
                    .filter(
                        item =>
                            this.itemHasPackingProcess(item) &&
                            (item.packingQty || item.suggestedQty)
                    )
                    .map(item => ({
                        partNumberId: item.partNumberId,
                        quantity: item.quantity,
                        mass: item.mass,
                        partNumber: item.partNumber,
                        packingQty: Number(item.packingQty),
                        packingMass:
                            Number(item.mass) * Number(item.packingQty),
                        description: item.description,
                        ...(item.notes !== undefined && { notes: item.notes }),
                        workOrder: workOrder.code,
                        suggestedQty: Number(item.suggestedQty),
                        finishAndUCCode: item.finishAndUCCode,
                        ...(item.oc !== undefined && { oc: item.oc }),
                        ...(item.costPosition !== undefined && {
                            costPosition: item.costPosition,
                        }),
                    }))
            })
            let packageBOMItems = this.selectedBOMs.flatMap(bom => {
                return bom.items
                    .filter(item => item.packingQty || item.suggestedQty)
                    .map(item => ({
                        code: item.code,
                        quantity: item.quantity,
                        packingQty: Number(item.packingQty),
                        packingMass:
                            Number(item.mass) * Number(item.packingQty),
                        description: item.description,
                        notes: item.notes,
                        bom: bom.code,
                        suggestedQty: item.suggestedQty,
                        mass: item.mass,
                    }))
            })
            const packageData = {
                items: [...packageWOItems, ...packageBOMItems],
                packageDimensions: this.packageDimensions,
                packageBOM: this.packageBOM,
            }

            this.structuredData = packageData
            return packageData
        },
        itemHasPackingProcess(item) {
            return (
                Array.isArray(item?.processes) &&
                item.processes.includes('packing')
            )
        },
        packingProcessItems(workOrder) {
            return (workOrder.items || []).filter(item =>
                this.itemHasPackingProcess(item)
            )
        },
        sortedItems(workOrder, index) {
            return this.packingProcessItems(workOrder)
                .filter(i =>
                    i.partNumber.includes(this.partNumberToSearch[index] || '')
                )
                .sort((a, b) => b.availableQty - a.availableQty)
        },
        sortedBOMItems(bom) {
            return [...bom.items].sort(
                (a, b) => b.availableQty - a.availableQty
            )
        },
        openPackingQtyDialog(parentIndex, itemIndex, type, item) {
            this.lockShouldBeHeld = true
            this.updatePackageLock(true)
            this.packingQtyDialog = true
            this.selectedParentIndex = parentIndex
            this.selectedItemIndex = itemIndex
            this.selectedType = type
            this.selectedItem = _.cloneDeep(item)
        },
        updatePackingQty(newPackingQty) {
            if (this.selectedType == 'bom') {
                //code
                const index = this.selectedBOMs[
                    this.selectedParentIndex
                ].items.findIndex(i => i.code == this.selectedItem.code)
                this.selectedBOMs[this.selectedParentIndex].items[
                    index
                ].packingQty = newPackingQty
            } else if (this.selectedType == 'wo') {
                const index = this.selectedWorkOrders[
                    this.selectedParentIndex
                ].items.findIndex(
                    i => i.partNumberId == this.selectedItem.partNumberId
                )
                this.selectedWorkOrders[this.selectedParentIndex].items[
                    index
                ].packingQty = newPackingQty
            }
            this.structureData()
            this.closePackingQtyDialog()
        },
        closePackingQtyDialog() {
            this.selectedParentIndex = -1
            this.selectedItemIndex = -1
            this.selectedType = ''
            this.selectedItem = {}
            this.packingQtyDialog = false
        },
    },
}
</script>

<style>
#virtual-scroll-table {
    width: 100%;
    overflow: scroll;
}
</style>
